import { useCallback } from "react";
import { prepareWriteContract, writeContract } from "wagmi/actions";
import { getTokenAddressBySymbol } from "../utils/orderUtils";
import { Address, Hash } from "viem";
import { GlobalStore } from "../store/global/GlobalStore";
import { getWrappedSymbolForChain } from "../utils/chainUtils";
import { usePublicClient, useWalletClient } from "wagmi";
import { WRAPPED_ABI } from "../config/consts";
import { useTokenListByChain } from "./useTokenListByChain";

export const useWrap = () => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const tokenList = useTokenListByChain();
  const { wrapQuote, network, address } = GlobalStore.useState(s => {
    return {
      wrapQuote: s.wrapQuote,
      network: s.network,
      address: s.address,
    };
  });

  return useCallback(
    async (amount: bigint) => {
      const wrappedToken = getWrappedSymbolForChain(network);
      let txHash: Hash | undefined = undefined;
      if (!wrapQuote || !walletClient) {
        throw new Error(
          `Could not find: ${!wrapQuote ? "quote," : ""} ${!walletClient ? "wallet" : ""}`
        );
      }
      if (network.id !== walletClient.chain.id && network.id !== publicClient.chain.id) {
        throw new Error(`Wrong network`);
      }
      if (wrapQuote.type === "Wrapping") {
        const config = await prepareWriteContract({
          address: getTokenAddressBySymbol(tokenList, network, wrappedToken),
          account: address as Address,
          abi: WRAPPED_ABI,
          functionName: "deposit",
          chainId: network.id, // Important - this binds the contract to the correct network
          value: amount,
        });
        const { hash } = await writeContract({ ...config, chainId: network.id });
        txHash = hash;
      } else {
        const config = await prepareWriteContract({
          address: getTokenAddressBySymbol(tokenList, network, wrappedToken),
          account: address as Address,
          abi: WRAPPED_ABI,
          functionName: "withdraw",
          chainId: network.id, // Important - this binds the contract to the correct network
          args: [amount],
        });
        const { hash } = await writeContract({ ...config, chainId: network.id });
        txHash = hash;
      }

      return txHash;
    },
    [network, wrapQuote, walletClient, publicClient.chain.id, tokenList, address]
  );
};
